'use client';

export interface SitePhrase {
  key: string;
  label: string;
  value: string;
  page: string;
  category: 'title' | 'description' | 'label' | 'button' | 'phrase';
}

export const defaultSitePhrases: SitePhrase[] = [
  // Auth Page - Left Panel
  { key: 'auth.brand.name', label: 'Brand Name', value: 'CVMaids', page: 'Auth / Login', category: 'title' },
  { key: 'auth.hero.title.line1', label: 'Hero Title Line 1', value: "Africa's Trusted", page: 'Auth / Login', category: 'title' },
  { key: 'auth.hero.title.highlight', label: 'Hero Title Highlight', value: 'Home Manager', page: 'Auth / Login', category: 'title' },
  { key: 'auth.hero.title.line2', label: 'Hero Title Line 2', value: 'Platform', page: 'Auth / Login', category: 'title' },
  { key: 'auth.hero.subtitle', label: 'Hero Subtitle', value: 'Connect with verified Home Managers, track performance, and manage recruitment — all from one trusted portal.', page: 'Auth / Login', category: 'description' },
  { key: 'auth.stats.count', label: 'Stats Count', value: '2,400+ Home Managers', page: 'Auth / Login', category: 'phrase' },
  { key: 'auth.stats.subtitle', label: 'Stats Subtitle', value: 'registered across Southern Africa', page: 'Auth / Login', category: 'phrase' },
  { key: 'auth.feature1', label: 'Feature 1', value: 'Verified Home Manager profiles with full employment history', page: 'Auth / Login', category: 'phrase' },
  { key: 'auth.feature2', label: 'Feature 2', value: 'Transparent performance reviews and star ratings', page: 'Auth / Login', category: 'phrase' },
  { key: 'auth.feature3', label: 'Feature 3', value: 'Recruitment, termination, and status management in one place', page: 'Auth / Login', category: 'phrase' },
  { key: 'auth.feature4', label: 'Feature 4', value: 'Directory of Available and Unavailable Home Managers', page: 'Auth / Login', category: 'phrase' },
  // Auth Page - Right Panel
  { key: 'auth.login.title', label: 'Login Title', value: 'Welcome back', page: 'Auth / Login', category: 'title' },
  { key: 'auth.login.subtitle', label: 'Login Subtitle', value: 'Sign in to your CVMaids account', page: 'Auth / Login', category: 'description' },
  { key: 'auth.register.title', label: 'Register Title', value: 'Create your account', page: 'Auth / Login', category: 'title' },
  { key: 'auth.register.subtitle', label: 'Register Subtitle', value: 'Choose your account type to get started', page: 'Auth / Login', category: 'description' },
  { key: 'auth.signin.button', label: 'Sign In Button', value: 'Sign In', page: 'Auth / Login', category: 'button' },
  { key: 'auth.register.button', label: 'Register Button', value: 'Register', page: 'Auth / Login', category: 'button' },
  { key: 'auth.employer.tab', label: 'Employer Tab', value: 'As Employer', page: 'Auth / Login', category: 'label' },
  { key: 'auth.hm.tab', label: 'Home Manager Tab', value: 'As Home Manager', page: 'Auth / Login', category: 'label' },
  { key: 'auth.terms.text', label: 'Terms Text', value: 'By continuing, you agree to CVMaids\'', page: 'Auth / Login', category: 'phrase' },
  // Welcome Page
  { key: 'welcome.hero.greeting', label: 'Hero Greeting', value: 'Welcome back,', page: 'Welcome', category: 'title' },
  { key: 'welcome.hero.subtitle', label: 'Hero Subtitle', value: 'Here\'s what\'s happening on your CVMaids platform today.', page: 'Welcome', category: 'description' },
  { key: 'welcome.section.title', label: 'Section Title', value: 'Quick Actions', page: 'Welcome', category: 'title' },
  // Members Directory
  { key: 'directory.title', label: 'Page Title', value: 'Members Directory', page: 'Members Directory', category: 'title' },
  { key: 'directory.subtitle', label: 'Page Subtitle', value: 'Browse and connect with Home Managers across Southern Africa', page: 'Members Directory', category: 'description' },
  { key: 'directory.search.placeholder', label: 'Search Placeholder', value: 'Search by name, city, or skill...', page: 'Members Directory', category: 'label' },
  // Activity Feed
  { key: 'feed.title', label: 'Page Title', value: 'Activity Feed', page: 'Activity Feed', category: 'title' },
  { key: 'feed.subtitle', label: 'Page Subtitle', value: 'Stay updated with the latest activity from your network', page: 'Activity Feed', category: 'description' },
  // Home Manager Profile
  { key: 'profile.recruit.button', label: 'Recruit Button', value: 'Recruit', page: 'Home Manager Profile', category: 'button' },
  { key: 'profile.terminate.button', label: 'Terminate Button', value: 'Terminate', page: 'Home Manager Profile', category: 'button' },
  { key: 'profile.overview.tab', label: 'Overview Tab', value: 'Overview', page: 'Home Manager Profile', category: 'label' },
  { key: 'profile.reviews.tab', label: 'Reviews Tab', value: 'Reviews', page: 'Home Manager Profile', category: 'label' },
  { key: 'profile.employment.tab', label: 'Employment Tab', value: 'Employment', page: 'Home Manager Profile', category: 'label' },
  // Admin Panel
  { key: 'admin.dashboard.title', label: 'Dashboard Title', value: 'Dashboard', page: 'Admin Panel', category: 'title' },
  { key: 'admin.dashboard.subtitle', label: 'Dashboard Subtitle', value: 'Site overview and moderation tools', page: 'Admin Panel', category: 'description' },
  { key: 'admin.panel.label', label: 'Admin Panel Label', value: 'Admin Panel', page: 'Admin Panel', category: 'label' },
  { key: 'admin.subscriptions.title', label: 'Subscriptions Title', value: 'Subscription Plans', page: 'Admin Panel', category: 'title' },
  { key: 'admin.roles.title', label: 'Roles Title', value: 'Roles & Features', page: 'Admin Panel', category: 'title' },
];

const STORAGE_KEY = 'cvmaids_site_phrases';

export function getSitePhrases(): SitePhrase[] {
  if (typeof window === 'undefined') return defaultSitePhrases;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return defaultSitePhrases;
    const parsed: SitePhrase[] = JSON.parse(stored);
    // Merge: add any new default keys not in stored
    const storedKeys = new Set(parsed.map((p) => p.key));
    const merged = [...parsed];
    for (const def of defaultSitePhrases) {
      if (!storedKeys.has(def.key)) merged.push(def);
    }
    return merged;
  } catch {
    return defaultSitePhrases;
  }
}

export function saveSitePhrases(phrases: SitePhrase[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(phrases));
}

export function getPhrase(key: string, phrases?: SitePhrase[]): string {
  const list = phrases || defaultSitePhrases;
  return list.find((p) => p.key === key)?.value ?? key;
}

// Image storage key for maid background
export const MAID_BG_KEY = 'cvmaids_maid_bg_image';

export function getMaidBgImage(): string {
  if (typeof window === 'undefined') return '/assets/images/maid_background.png';
  return localStorage.getItem(MAID_BG_KEY) || '/assets/images/maid_background.png';
}

export function saveMaidBgImage(dataUrl: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(MAID_BG_KEY, dataUrl);
}
